import React, { useImperativeHandle, useRef, useEffect } from "react";
import '../../styles/main.css';
import CxSnackbar from './CxSnackbar';
import { useTranslation } from '../hx/useTranslation';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import * as FxLog from '../fx/FxLog';
import { useWindowSize } from '../hx/useWindowSize';
import { MdRecordVoiceOver } from "react-icons/md";

const CxSpeech = props => {
  const size = useWindowSize();
  const [{ trans }] = useTranslation();
  const { id, setVal, onSpeechFinish } = props;

  const r_active_id = useRef(null);
  const r_text_base = useRef(""); 
  const r_snack_speak_now = useRef(null);
  const r_snack_browser_does_not_support = useRef(null);
  const r_timeout_stop = useRef(null);
  const r_is_processing = useRef(false);
  const r_is_starting = useRef(false); // <-- AÑADIDO: Controla el encendido asíncrono

  const {
    transcript: srecon_transcript,
    listening: srecon_listening,
    resetTranscript,
    browserSupportsSpeechRecognition: srecon_browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  const setGlobalSelection = (enable) => {
    const value = enable ? "auto" : "none";
    document.body.style.userSelect = value;
    document.body.style.webkitUserSelect = value;
    if (!enable) window.getSelection()?.removeAllRanges(); 
  };

  // ÚNICO MANEJADOR: Escribe solo cuando termina de escuchar (Unificado con Whisper)
  useEffect(() => {
    // 1. Si ya está escuchando, marcamos el encendido como exitoso y salimos
    if (srecon_listening) {
      r_is_starting.current = false;
      return;
    }

    // 2. Si NO está escuchando pero está en proceso de arrancar, ignoramos (evita el fallo del primer clic)
    if (r_is_starting.current) return;

    // 3. Ignorar si no hay sesión activa
    if (r_active_id.current !== id) return;

    const transcriptFinal = srecon_transcript.trim();
    
    if (transcriptFinal !== "") {
      const base = r_text_base.current || "";
      const separator = (base && !base.endsWith(" ")) ? " " : "";
      const newText = base + separator + transcriptFinal;
      
      setVal(newText);
      setTimeout(() => {
        if (onSpeechFinish) onSpeechFinish(newText);
      }, 0);      
    }

    setGlobalSelection(true); 
    r_snack_speak_now.current?.setOpen(false);
    r_active_id.current = null;

  }, [srecon_listening, id, onSpeechFinish, setVal, srecon_transcript]);

  useImperativeHandle(props.xref, () => ({
    getType: () => 'CxSpeech',
    doStartStop,
    reset: () => {
      resetTranscript();
      r_active_id.current = null;
      r_text_base.current = "";
    }
  }));

  const doStartStop = (activeId, boStart) => {
    if (!SpeechRecognition || !srecon_browserSupportsSpeechRecognition) {
      r_snack_browser_does_not_support.current?.setOpen(true);
      return;
    }

    if (boStart) {
      // CAPA 1: Limpiamos cualquier orden de apagado pendiente
      if (r_timeout_stop.current) {
        clearTimeout(r_timeout_stop.current);
        r_timeout_stop.current = null;
      }

      // CAPA 2: Si ya está escuchando activamente, NO reiniciamos el motor.
      if (srecon_listening) {
         r_snack_speak_now.current?.setOpen(true);
         return; 
      }

      // CAPA 3: Evitamos que dos eventos simultáneos (ej. Touch + Clic) ejecuten esto a la vez
      if (r_is_processing.current) return;
      r_is_processing.current = true;

      setGlobalSelection(false); 
      const currentInput = document.getElementById(activeId);
      r_text_base.current = currentInput ? currentInput.value : "";
      
      resetTranscript();
      r_active_id.current = activeId;
      
      try {
        r_is_starting.current = true; // <-- AÑADIDO: Avisamos que arranca
        SpeechRecognition.startListening({ 
          continuous: size.isLarge, 
          language: 'es-ES',
          interimResults: true, 
        });
        r_snack_speak_now.current?.setOpen(true);
      } catch (err) {
        FxLog.infox('CxSpeech.startError', err);
        setGlobalSelection(true);
        r_is_starting.current = false; // <-- AÑADIDO: Si falla, reseteamos
      } finally {
        // Liberamos el cerrojo tras un breve margen (300ms)
        setTimeout(() => { r_is_processing.current = false; }, 300);
      }

    } else {
      setGlobalSelection(true); 
      
      // Retrasamos el apagado para dar margen al sistema y permitir empalmes rápidos
      r_timeout_stop.current = setTimeout(() => {
        SpeechRecognition.stopListening();
        r_snack_speak_now.current?.setOpen(false);
        r_timeout_stop.current = null;
      }, 500); 
    }
  };

  if (!SpeechRecognition) return null;

  return (
    <>
      <CxSnackbar 
        xref={r_snack_speak_now} 
        severity="warning" 
        autoHideDuration={null} 
        hideIcon={true} 
        value={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#fff' }}>
            <MdRecordVoiceOver fontSize={22} className="blink" /> 
            <span>{trans('msg.speak_now')}</span>
          </div>
        } 
      />

      <CxSnackbar 
        xref={r_snack_browser_does_not_support} 
        severity="error" 
        value={trans('msg.browser_doesnt_support_x', 'field.voice_recognition')} 
      />
    </>
  );
};

export default CxSpeech;